<template>
  <div
    id="leaderSimple"
    class="leaderSimple mainCard"
    :class="{detailsView: isDetailsView, listView: !isDetailsView}"
  >
    <!-- Desktop layouts -->
    <el-row
      v-if="['desktop', 'laptop'].includes($mq)"
      :gutter="5"
      type="flex"
    >
      <el-col :span="responsiveCol.one">
        <el-card
          class="clear-card leaderDetail"
          shadow="hover"
          :body-style="bodyStyle"
        >
          <div class="name">
            {{ leader.first_name }} {{ leader.last_name }}
          </div>
          <div
            v-if="isDetailsView"
            class="detailBtn"
          >
            <el-button
              style="margin-left: 0; margin-top: 5px; white-space: normal; word-break: break-word;"
              type="primary"
              round
              @click="showDetailsDialog = true"
            >
              {{ $t('settings.viewDetails') }}
            </el-button>
          </div>
        </el-card>
      </el-col>
      <el-col :span="responsiveCol.two">
        <Category :tier="leader.tier" />
      </el-col>
      <el-col :span="responsiveCol.three">
        <Counter
          :label="`${$t('session.sessionCap')} / ${$t('common.duration')}`"
          zero-label="-"
          :value="session"
          :value-total="leader.duration"
        />
      </el-col>
      <el-col :span="responsiveCol.four">
        <Meeting :meeting="leader.next_meeting" />
      </el-col>
    </el-row>

    <!-- Mobile layout list -->
    <el-card
      v-else-if="['mobile', 'tablet'].includes($mq) && !isDetailsView"
      class="clear-card leaderListMobile"
      shadow="hover"
      :body-style="smallListStyle"
    >
      <el-row class="colLine leaderInfo">
        <el-col
          :span="16"
          class="nameSmall"
        >
          {{ leader.first_name }} {{ leader.last_name }}
        </el-col>
        <el-col :span="8">
          <div class="sessionSmall">
            <div>
              {{ session !== 0
                ? `${$t('session.sessionCap')} ${session}` : $t('session.introductory') }}
            </div>
          </div>
        </el-col>
      </el-row>
      <el-row class="colLine leaderTime">
        <el-col
          :span="12"
          class="meetingLabel"
        >
          {{ $t('coach.leader.nextMeeting') }}
        </el-col>
        <el-col
          :span="12"
          class="meetingTime"
        >
          <span
            v-if="!leader.next_meeting || leader.next_meeting.includes('Next')"
            class="noMeeting"
          >
            {{ $t('coach.leader.noMeetingBooked') }}
          </span>
          <span
            v-else
          >
            {{ leader.next_meeting }}
          </span>
        </el-col>
      </el-row>
    </el-card>

    <!-- Mobile layout detail -->
    <div
      v-else
      class="leaderDetailMobile"
    >
      <div class="customPlate left">
        <el-card
          class="clear-card detailCard"
          shadow="hover"
          :body-style="smallDetailStyle"
        >
          <div class="cardTitle">
            {{ $t('common.name') }}
          </div>
          <div class="cardContent">
            {{ leader.full_name }}
          </div>
        </el-card>
      </div>
      <div class="customPlate right">
        <el-card
          class="clear-card detailCard"
          shadow="hover"
          :body-style="smallDetailStyle"
        >
          <div class="cardTitle">
            {{ $t('coach.leader.category.title') }}
          </div>
          <div class="cardContent">
            <template v-if="formattedCategory">
              {{ formattedCategory }}
            </template>
            <template v-else>
              {{ $t(`coachingTier.tier_${leader.tier}`) }}
              {{ $t(`coachingTier.tier_${leader.tier}_caption`) }}
            </template>
          </div>
        </el-card>
      </div>
      <div class="customPlate left">
        <el-card
          class="clear-card detailCard"
          shadow="hover"
          :body-style="smallDetailStyle"
        >
          <div class="cardTitle">
            {{ $t('session.sessionCap') }} / {{ $t('common.duration') }}
          </div>
          <div class="cardContentBig">
            {{ session }} / {{ leader.duration }}
          </div>
        </el-card>
      </div>
      <div class="customPlate right">
        <el-card
          class="clear-card detailCard"
          shadow="hover"
          :body-style="smallDetailStyle"
        >
          <div class="cardTitle">
            {{ $t('coach.leader.nextMeeting') }}
          </div>
          <div class="cardContent">
            <span
              v-if="leader.next_meeting.includes('Next')"
              class="noMeeting"
            >
              {{ $t('coach.leader.noMeetingBooked') }}
            </span>
            <span
              v-else
            >
              {{ leader.next_meeting }}
            </span>
          </div>
        </el-card>
      </div>
    </div>

    <DetailsDialog
      :show.sync="showDetailsDialog"
      :leader.sync="leaderLocal"
      @duration-changed="durationChanged"
    />
  </div>
</template>

<script>
import Counter from './widgets/Counter.vue';
import Meeting from './widgets/Meeting.vue';
import Category from './widgets/Category.vue';
import DetailsDialog from './widgets/DetailsModal.vue';

export default {
  components: {
    Counter,
    Meeting,
    Category,
    DetailsDialog,
  },

  props: {
    leader: {
      type: Object,
      default: null,
    },

    isDetailsView: {
      type: Boolean,
      default: false,
    },

    role: {
      type: String,
      default: 'coach',
    },

    meetings: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      bodyStyle: {
        // height: 'calc(100% - 40px)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
      smallListStyle: {
        padding: '0',
      },
      smallDetailStyle: {
        height: '100%',
        padding: '20px 2px',
      },
      showDetailsDialog: false,
    };
  },

  computed: {
    leaderLocal: {
      get() {
        return this.leader;
      },
      set(newValue) {
        this.$emit('update:leader', newValue);
      },
    },

    session() {
      const tmp = this.leader.session_id.split('_');
      let result = 0;
      if (parseInt(tmp[1], 10) > -1) {
        result = parseInt(tmp[1], 10);
      }
      return result;
    },

    responsiveCol() {
      const col = {};
      // if (this.isDetailsView) {
      col.one = 5;
      col.two = 9;
      col.three = 5;
      col.four = 5;
      // } else {
      //   // col.one = 5;
      //   // col.two = 5;
      //   // col.three = 9;
      //   col.one = 6;
      //   col.two = 6;
      //   col.three = 6;
      //   col.four = 6;
      // }
      return col;
    },

    formattedCategory() {
      let defaultCategory = null;

      if (process.env.VUE_APP_SERVICE === 'ing_gcg_platform') {
        defaultCategory = this.$t('coachingTier.ingLeaders');
      }

      return defaultCategory;
    },
  },

  methods: {
    goToGla() {
      window.open(`${process.env.VUE_APP_GLA_CLIENT}/dashboard/assessment/${this.leader.gla_uuid}`, '_blank');
    },

    durationChanged() {
      this.$emit('duration-changed');
    },
  },
};
</script>

<style lang="scss" scoped>
  #leaderSimple {
    .noMeeting {
      font-weight: normal;
      color: #c4c4c4;
    }

    .detailBtn {
      .el-button--plain {
        border-color: white;
      }

      .el-button--primary {
        background: transparent;
        border-color: var(--themeFontColor);
      }
    }

    .el-row {
      .el-card {
        border-radius: 0;
        height: 100%;
        min-height: 154px;

        &.leaderDetail {
          text-align: center;

          .name {
            font-weight: bold;
            margin-bottom: 5px;
            word-break: break-word;
          }

          .detailBtn {
            width: 100%;
            margin-top: 10px;

            .el-button {
              width: 83%;
              padding: 13px 5px;
              color: var(--themeFontColor);
            }
          }

          .position {
            margin-bottom: 15px;
          }
        }
      }

      margin-bottom: 15px;

      .el-col:first-child {
        .el-card {
          border-top-left-radius: 10px;
          border-bottom-left-radius: 10px;
        }
      }

      .el-col:last-child {
        .el-card {
          border-top-right-radius: 10px;
          border-bottom-right-radius: 10px;
        }
      }
    }

    .detailsCard {
      margin-bottom: 20px !important;
      text-align: left;

      .el-row {
        margin-bottom: 0;
      }

      .title {
        font-size: 12px;
        margin-bottom: 10px;
      }

      .value {
        font-size: 18px;
        padding-bottom: 5px;
        word-break: break-word;

        &.noData {
          font-style: italic;
          color: #c4c4c4;
        }
      }
    }

    &.listView:hover .el-row .el-card {
      .el-card__header,
      .el-card__body {
        box-sizing: border-box;
        background-color: rgba(255, 255, 255, 0.2);
      }
    }

    .leaderListMobile {
      margin: 10px auto;

      .colLine {
        margin: 0;

        .nameSmall {
          padding: 20px;
          font-weight: bold;
        }

        .sessionSmall {
          padding: 20px 20px 20px 5px;
          text-align: right;
          word-break: break-word;
        }

        .meetingLabel {
          padding: 20px;
          border-top: 1px solid #c4c4c4;
          font-weight: bold;
        }

        .meetingTime {
          padding: 20px;
          border-top: 1px solid #c4c4c4;
          text-align: right;
        }
      }
    }

    .leaderDetailMobile {
      margin: 10px auto 15px;
      display: flex;
      flex-wrap: wrap;
      align-items: stretch;

      .customPlate {
        display: inline-block;
        box-sizing: border-box;
        vertical-align: middle;
        width: 50%;
        text-align: center;
        padding-top: 18px;

        &:first-child {
          padding-top: 0;
        }

        &:nth-child(2) {
          padding-top: 0;
        }

        .detailCard {
          width: 100%;
          height: 100%;

          .cardTitle {
            font-weight: bold;
          }

          .cardContentBig {
            font-size: 36px;
            padding: 10px 0;
            height: 100%;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .cardContent {
            padding: 20px 5px;
            word-break: break-word;
            height: 100%;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      }

      .left {
        padding-right: 5px;
      }

      .right {
        padding-left: 5px;
      }
    }
  }
</style>

<style lang="scss">
  #leaderSimple {
    .el-card__body {
      box-sizing: border-box;
    }
  }
</style>
